import { refreshAdmin } from './admin-auth-api';
import { readErrorMessage } from './http-json';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';

export interface AdminCoreMemoryItem {
  id: string;
  content: string;
  category: string;
  domain: string | null;
  sensitivity: string;
  temporalKind: string;
  status: string;
  sourceKind: string;
  userConfirmed: boolean;
  expiresAt: string | null;
  staleAfter: string | null;
  updatedAt: string;
  masked: boolean;
}

export interface AdminCoreMemorySnapshot {
  id: string;
  version: number;
  summaryText: string;
  reason: string;
  createdAt: string;
}

export interface AdminCoreMemoryDetail {
  consumerUserId: string;
  summaryText: string;
  currentVersion: number;
  refreshStatus: string;
  lastSourceMessageAt: string | null;
  lastSuccessfulRefreshAt: string | null;
  refreshRequestedAt: string | null;
  lastError: string | null;
  items: AdminCoreMemoryItem[];
  snapshots: AdminCoreMemorySnapshot[];
}

export interface AdminCoreMemoryAuditItem {
  id: string;
  actorUserId: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

interface ApiDetail {
  consumer_user_id: string;
  summary_text: string;
  current_version: number;
  refresh_status: string;
  last_source_message_at: string | null;
  last_successful_refresh_at: string | null;
  refresh_requested_at: string | null;
  last_error: string | null;
  items: Array<{
    id: string; content: string; category: string; domain: string | null; sensitivity: string;
    temporal_kind: string; status: string; source_kind: string; user_confirmed: boolean;
    expires_at: string | null; stale_after: string | null; updated_at: string; masked: boolean;
  }>;
  snapshots: Array<{ id: string; version: number; summary_text: string; reason: string; created_at: string }>;
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = refreshAdmin()
    .then((session) => {
      useAdminAuthStore.getState().setSession(session.accessToken, session.user);
      return session.accessToken;
    })
    .catch(() => {
      useAdminAuthStore.getState().clearSession();
      return null;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

async function requestAdmin<T>(accessToken: string, path: string, init: RequestInit, label: string): Promise<T> {
  const send = (token: string) => fetch(path, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  let response = await send(accessToken);
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) response = await send(refreshed);
  }
  if (!response.ok) throw new Error(`${label} failed (${response.status}): ${await readErrorMessage(response)}`);
  return response.json() as Promise<T>;
}

function baseUrl(consumerUserId: string): string {
  return `/api/v1/admin/user-core-memories/${encodeURIComponent(consumerUserId)}`;
}

function normalize(payload: ApiDetail): AdminCoreMemoryDetail {
  return {
    consumerUserId: payload.consumer_user_id,
    summaryText: payload.summary_text,
    currentVersion: payload.current_version,
    refreshStatus: payload.refresh_status,
    lastSourceMessageAt: payload.last_source_message_at,
    lastSuccessfulRefreshAt: payload.last_successful_refresh_at,
    refreshRequestedAt: payload.refresh_requested_at,
    lastError: payload.last_error,
    items: payload.items.map((item) => ({
      id: item.id, content: item.content, category: item.category, domain: item.domain,
      sensitivity: item.sensitivity, temporalKind: item.temporal_kind, status: item.status,
      sourceKind: item.source_kind, userConfirmed: item.user_confirmed, expiresAt: item.expires_at,
      staleAfter: item.stale_after, updatedAt: item.updated_at, masked: item.masked,
    })),
    snapshots: payload.snapshots.map((snapshot) => ({
      id: snapshot.id, version: snapshot.version, summaryText: snapshot.summary_text,
      reason: snapshot.reason, createdAt: snapshot.created_at,
    })),
  };
}

export async function getAdminCoreMemory(accessToken: string, consumerUserId: string): Promise<AdminCoreMemoryDetail> {
  return normalize(await requestAdmin<ApiDetail>(accessToken, baseUrl(consumerUserId), { method: 'GET' }, 'Load core memory'));
}

export async function revealAdminCoreMemory(accessToken: string, consumerUserId: string, reason: string): Promise<AdminCoreMemoryDetail> {
  return normalize(await requestAdmin<ApiDetail>(accessToken, `${baseUrl(consumerUserId)}/sensitive-view`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  }, 'Reveal sensitive core memory'));
}

export async function regenerateAdminCoreMemory(accessToken: string, consumerUserId: string, reason: string): Promise<void> {
  await requestAdmin<unknown>(accessToken, `${baseUrl(consumerUserId)}/regenerate`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  }, 'Regenerate core memory');
}

export async function deleteAdminCoreMemoryItem(accessToken: string, consumerUserId: string, memoryId: string, reason: string): Promise<void> {
  await requestAdmin<unknown>(accessToken, `${baseUrl(consumerUserId)}/items/${encodeURIComponent(memoryId)}`, {
    method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  }, 'Delete core memory item');
}

export async function restoreAdminCoreMemorySnapshot(accessToken: string, consumerUserId: string, snapshotId: string, reason: string): Promise<void> {
  await requestAdmin<unknown>(accessToken, `${baseUrl(consumerUserId)}/snapshots/${encodeURIComponent(snapshotId)}/restore`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  }, 'Restore core memory snapshot');
}

export async function listAdminCoreMemoryAudits(accessToken: string, consumerUserId: string): Promise<AdminCoreMemoryAuditItem[]> {
  const payload = await requestAdmin<{
    items: Array<{ id: string; actor_user_id: string | null; action: string; target_type: string; target_id: string | null; metadata: Record<string, unknown>; created_at: string }>;
  }>(accessToken, '/api/v1/admin/audit-logs?limit=100&offset=0', { method: 'GET' }, 'Load core memory audit logs');
  return payload.items
    .filter((item) => item.action.startsWith('user_core_memory.'))
    .filter((item) => item.target_id === consumerUserId || item.metadata.consumer_user_id === consumerUserId)
    .map((item) => ({
      id: item.id, actorUserId: item.actor_user_id, action: item.action, targetType: item.target_type,
      targetId: item.target_id, metadata: item.metadata, createdAt: item.created_at,
    }));
}
